/**
 * Redux slice (src/store/slices/ghostLoginSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import instance from '../../helpers/axiosconfig';

const firstNonEmptyText = (...values) => {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text && text.toLowerCase() !== 'undefined' && text.toLowerCase() !== 'null') {
      return text;
    }
  }
  return '';
};

const GHOST_USER_INFO_STORAGE_KEY = 'ghostUserInfo';

// A local abort does not mean the server finished changing its cookie. This
// event carries no identity/authority; it only permits a fresh server read.
export const ghostTransportSettled = createAction('ghostLogin/transportSettled');

export const sanitizeGhostUserInfo = (user) => {
  if (!user || typeof user !== 'object') return null;

  const firstName = firstNonEmptyText(user.firstName, user.FirstName);
  const lastName = firstNonEmptyText(user.lastName, user.LastName);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const displayName = firstNonEmptyText(user.displayName, user.Username, user.Name, fullName);
  const userType = firstNonEmptyText(user.UserType, user.userType);
  const language = firstNonEmptyText(user.language, user.Language, user.locale, user.preferredLanguage, user.lang);
  const timezone = firstNonEmptyText(user.Timezone, user.userTimezone, user.timezone);
  const timeFormat = firstNonEmptyText(user.timeFormat, user.hourFormat);

  const safeUser = {};
  if (userType) {
    safeUser.UserType = userType;
    safeUser.userType = userType;
  }
  if (displayName) {
    safeUser.displayName = displayName;
    safeUser.Username = displayName;
    safeUser.Name = displayName;
  }
  if (firstName) safeUser.firstName = firstName;
  if (lastName) safeUser.lastName = lastName;
  if (language) safeUser.language = language;
  if (timezone) safeUser.Timezone = timezone;
  if (timeFormat) safeUser.timeFormat = timeFormat;

  return Object.keys(safeUser).length > 0 ? safeUser : null;
};

const persistGhostSessionMetadata = (userType, userInfo) => {
  const safeUserInfo = sanitizeGhostUserInfo(userInfo);
  const normalizedUserType = firstNonEmptyText(userType, safeUserInfo?.UserType, safeUserInfo?.userType).toLowerCase();

  localStorage.setItem('ghostMode', 'true');
  if (normalizedUserType) localStorage.setItem('ghostUserType', normalizedUserType);
  else localStorage.removeItem('ghostUserType');

  if (safeUserInfo) localStorage.setItem(GHOST_USER_INFO_STORAGE_KEY, JSON.stringify(safeUserInfo));
  else localStorage.removeItem(GHOST_USER_INFO_STORAGE_KEY);
};

const sanitizeOriginalAdminInfo = (admin) => {
  const adminData = admin && typeof admin === 'object' ? admin : {};
  const safeAdmin = sanitizeGhostUserInfo({
    ...adminData,
    UserType: 'admin',
    userType: 'admin',
  });

  return safeAdmin || {
    UserType: 'admin',
    userType: 'admin',
    displayName: 'Admin',
    Username: 'Admin',
    Name: 'Admin',
  };
};

// Async thunk for ghost login
/**
 * Async Redux thunk: Ghost Login. (ghostLogin/login)
 *
 * Calls the backend via the shared axios helper and returns a payload consumed by slice extraReducers.
 */
export const ghostLogin = createAsyncThunk(
  'ghostLogin/login',
  async ({ userId, userType }, { rejectWithValue, dispatch, requestId }) => {
    try {
      const response = await instance.post('/admin/ghost-login', {
        userId,
        userType
      });
      if (response.data?.success !== true || response.data?.session?.mode !== 'ghost'
        || response.data?.session?.userType !== userType
        || response.data?.session?.access !== 'full'
        || typeof userId !== 'string' || !/^[a-f0-9]{24}$/i.test(userId)
        || !['student', 'teacher', 'accountant'].includes(userType)) {
        return rejectWithValue('Ghost session was not confirmed');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Ghost login failed'
      );
    } finally {
      dispatch(ghostTransportSettled({ requestId }));
    }
  }
);

// Async thunk for exiting ghost mode
/**
 * Async Redux thunk: Exit Ghost Mode Async. (ghostLogin/exit)
 *
 * Calls the backend via the shared axios helper and returns a payload consumed by slice extraReducers.
 */
export const exitGhostModeAsync = createAsyncThunk(
  'ghostLogin/exit',
  async (_, { rejectWithValue, dispatch, requestId }) => {
    try {
      // BUG-004/005: Admin session is restored via the httpOnly adminToken cookie
      // set by the backend during ghost login. No need to send token in body.
      const response = await instance.post('/admin/exit-ghost-mode', {});
      if (response.data?.success !== true) {
        return rejectWithValue('Ghost exit was not confirmed');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to exit ghost mode'
      );
    } finally {
      dispatch(ghostTransportSettled({ requestId }));
    }
  }
);

// Helper function to get initial state from localStorage
/**
 * Get Initial State.
 *
 * Redux reducer: updates slice state in response to an action.
 */
const getInitialState = () => {
  const isGhostMode = localStorage.getItem('ghostMode') === 'true';
  let ghostUserInfo = null;
  if (isGhostMode) {
    const userData = localStorage.getItem(GHOST_USER_INFO_STORAGE_KEY);
    if (userData) {
      try {
        ghostUserInfo = sanitizeGhostUserInfo(JSON.parse(userData));
      } catch (e) {
        console.error('Failed to parse ghost user data:', e);
      }
    }
  }

  return {
    isGhostMode,
    // BUG-004/005: Admin token is now in httpOnly cookie only, not in localStorage.
    originalAdminToken: null,
    originalAdminData: null,
    ghostUserInfo,
    // Deliberately never restored from or written to browser storage.
    targetBinding: null,
    transitionRequestId: null,
    loading: false,
    error: null,
  };
};

/**
 * RTK slice definition: ghostLoginSlice.
 */
const ghostLoginSlice = createSlice({
  name: 'ghostLogin',
  initialState: getInitialState(),
  reducers: {
    // Activate ghost mode with existing token
    /**
     * Reducer: activateGhostMode.
     */
    activateGhostMode: (state, action) => {
      const userInfo = sanitizeGhostUserInfo(action.payload?.userInfo);
      state.isGhostMode = true;
      state.targetBinding = null;
      state.transitionRequestId = null;
      state.loading = false;
      // Tokens and raw Mongo/user documents must never be copied into Redux.
      // The active/restored sessions are carried by httpOnly cookies.
      state.originalAdminToken = null;
      state.originalAdminData = sanitizeOriginalAdminInfo(action.payload?.adminData);
      state.ghostUserInfo = userInfo;
      state.error = null;
      persistGhostSessionMetadata(action.payload?.userType, userInfo);
    },

    // Exit ghost mode and restore admin session
    /**
     * Reducer: exitGhostMode.
     */
    exitGhostMode: (state) => {
      state.isGhostMode = false;
      state.targetBinding = null;
      state.transitionRequestId = null;
      state.loading = false;
      state.ghostUserInfo = null;
      state.error = null;
      // Keep originalAdminToken for restoration
    },

    // Clear all ghost login data
    /**
     * Reducer: clearGhostData.
     */
    clearGhostData: (state) => {
      state.isGhostMode = false;
      state.originalAdminToken = null;
      state.originalAdminData = null;
      state.targetBinding = null;
      state.transitionRequestId = null;
      state.loading = false;
      state.ghostUserInfo = null;
      state.error = null;
      localStorage.removeItem('ghostMode');
      localStorage.removeItem('ghostUserType');
      localStorage.removeItem(GHOST_USER_INFO_STORAGE_KEY);
      localStorage.removeItem('originalAdminToken');
      localStorage.removeItem('originalAdminData');
    },

    /**
     * Reducer: setError.
     */
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // extraReducers handler: ghostLogin.pending
      .addCase(ghostLogin.pending, (state, action) => {
        state.transitionRequestId = action.meta.requestId;
        state.targetBinding = null;
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: ghostLogin.fulfilled
      .addCase(ghostLogin.fulfilled, (state, action) => {
        if (state.transitionRequestId !== action.meta.requestId) return;
        state.transitionRequestId = null;
        state.targetBinding = { userId: action.meta.arg.userId, userType: action.meta.arg.userType, requestId: action.meta.requestId };
        state.loading = false;
        state.isGhostMode = true;
        state.ghostUserInfo = sanitizeGhostUserInfo(action.payload.user);
        persistGhostSessionMetadata(
          action.meta?.arg?.userType || action.payload?.user?.UserType || action.payload?.user?.userType,
          state.ghostUserInfo,
        );

        // BUG-004/005: Tokens are now in httpOnly cookies only.
        // The backend sets the ghost token cookie and preserves the admin token
        // in a separate httpOnly adminToken cookie for restoration.
        state.originalAdminToken = null;

        // Store original admin data BEFORE clearing it (non-sensitive display data)
        const adminData = localStorage.getItem('Admins');
        let safeAdminData = null;
        if (adminData) {
          try {
            safeAdminData = sanitizeOriginalAdminInfo(JSON.parse(adminData));
          } catch (e) {
            console.error('Failed to parse admin display data:', e);
          }
        }
        state.originalAdminData = safeAdminData;
        if (safeAdminData) {
          localStorage.setItem('originalAdminData', JSON.stringify(safeAdminData));
        }
      })
      // extraReducers handler: ghostLogin.rejected
      .addCase(ghostLogin.rejected, (state, action) => {
        if (state.transitionRequestId !== action.meta.requestId) return;
        state.transitionRequestId = null;
        state.targetBinding = null;
        state.loading = false;
        state.error = action.payload;
      })
      // Exit ghost mode async thunk
      .addCase(exitGhostModeAsync.pending, (state, action) => {
        state.transitionRequestId = action.meta.requestId;
        state.targetBinding = null;
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: exitGhostModeAsync.fulfilled
      .addCase(exitGhostModeAsync.fulfilled, (state, action) => {
        if (state.transitionRequestId !== action.meta.requestId) return;
        state.transitionRequestId = null;
        state.targetBinding = null;
        state.loading = false;
        state.isGhostMode = false;
        state.ghostUserInfo = null;

        // BUG-004/005: The backend restores the admin httpOnly cookie automatically.
        // No token manipulation needed in localStorage.

        // Restore admin display data
        const adminData = localStorage.getItem('originalAdminData');
        if (adminData) {
          try {
            const safeAdminData = sanitizeOriginalAdminInfo(JSON.parse(adminData));
            localStorage.setItem('Admins', JSON.stringify(safeAdminData));
          } catch (e) {
            console.error('Failed to restore admin display data:', e);
            localStorage.removeItem('Admins');
          }
        }

        // Clean up ghost data (non-sensitive display data only)
        localStorage.removeItem('Students');
        localStorage.removeItem('Teachers');
        localStorage.removeItem('Accountants');
        localStorage.removeItem('ghostMode');
        localStorage.removeItem('ghostUserType');
        localStorage.removeItem(GHOST_USER_INFO_STORAGE_KEY);
        localStorage.removeItem('originalAdminToken');
        localStorage.removeItem('originalAdminData');

        // Clear Redux state
        state.originalAdminToken = null;
        state.originalAdminData = null;
      })
      // extraReducers handler: exitGhostModeAsync.rejected
      .addCase(exitGhostModeAsync.rejected, (state, action) => {
        if (state.transitionRequestId !== action.meta.requestId) return;
        state.transitionRequestId = null;
        state.targetBinding = null;
        state.loading = false;
        state.error = action.payload;
        console.error('❌ Failed to exit ghost mode:', action.payload);
      });
  },
});

export const {
  activateGhostMode,
  exitGhostMode,
  clearGhostData,
  setError
} = ghostLoginSlice.actions;

export default ghostLoginSlice.reducer;
